import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Styled from './styled';

// Lookup that returns IDs for error message strings when supplied a trip
// planner error code
const errorStringLookup = Object.freeze({
  SYSTEM_ERROR: {
    titleId: 'somethingwrong',
    bodyId: 'unavailable',
  },
  GRAPH_UNAVAILABLE: {
    titleId: 'somethingwrong',
    bodyId: 'unavailable',
  },
  OUTSIDE_BOUNDS: {
    titleId: 'cantplan',
    bodyId: 'outsideservicearea',
  },
  REQUEST_TIMEOUT: {
    titleId: 'somethingwrong',
    bodyId: 'requesttimeout',
  },
  BOGUS_PARAMETER: {
    titleId: 'cantplan',
    bodyId: 'invalidlink',
  },
  PATH_NOT_FOUND: {
    titleId: 'cantplan',
    bodyId: 'noservicelocation',
  },
  NO_PATHS_AFTER_FILTERING: {
    titleId: 'noservicetitle',
    bodyId: 'noservicemode',
  },
  NO_TRANSIT_TIMES: {
    titleId: 'cantplan',
    bodyId: 'noservicetime',
  },
  TOO_CLOSE: {
    titleId: 'cantplan',
    bodyId: 'endpointstooclose',
  },
  TRANSPORTATION_NETWORK_COMPANY_UNAVAILABLE: {
    titleId: 'noridesharetitle',
    bodyId: 'noridesharebody',
  },
  TRANSPORTATION_NETWORK_COMPANY_REQUEST_INVALID: {
    titleId: 'somethingwrong',
    bodyId: 'genericbody',
  },
  TRANSPORTATION_NETWORK_COMPANY_CONFIG_INVALID: {
    titleId: 'somethingwrong',
    bodyId: 'genericbody',
  },
});

function TripPlanError({
  planError,
  copySection,
  defaultTitleId,
  defaultBodyId,
}) {
  const { bodyId, titleId } = errorStringLookup[planError.message] || {};
  const intl = useIntl();
  const defaultBodyText = intl.formatMessage({
    id: `${copySection}.${defaultBodyId}`,
  });

  return (
    <Styled.TripPlanErrorWrapper>
      <Styled.TripPlanErrorTitleRow>
        <FormattedMessage
          id={`${copySection}.${titleId}`}
          defaultMessage={intl.formatMessage({
            id: `${copySection}.${defaultTitleId}`,
          })}
        />
      </Styled.TripPlanErrorTitleRow>
      <Styled.TripPlanErrorMessageRow>
        <FormattedMessage
          id={`${copySection}.${bodyId}`}
          defaultMessage={planError.msg || defaultBodyText}
        />
      </Styled.TripPlanErrorMessageRow>
    </Styled.TripPlanErrorWrapper>
  );
}

TripPlanError.propTypes = {
  /** Error object returned by trip planner engine */
  planError: PropTypes.shape({
    /** Message code indicating error type, used for lookups */
    message: PropTypes.string,
    /** Text describing error that occurred */
    msg: PropTypes.string,
  }).isRequired,
  /** Prefix used on error message IDs for lookups in strings file */
  copySection: PropTypes.string,
  /**
   * ID for value in strings file that will be used as for the title text if a
   * message specific to the supplied error type is not defined
   */
  defaultTitleId: PropTypes.string,
  /**
   * ID for value in strings file that will be used as the body text if a
   * message specific to the supplied error type isn't defined in this app nor
   * returned in the error object
   */
  defaultBodyId: PropTypes.string,
};

TripPlanError.defaultProps = {
  copySection: 'planner.error',
  defaultTitleId: 'somethingwrong',
  defaultBodyId: 'genericbody',
};

export default TripPlanError;
